import React, { useCallback, useEffect, useState } from "react";
import { Checkbox } from "../../common/UIComponents";
import { legSelectionStore, getAvailableLegs } from "./legSelectionStore";
import { useLegSelection } from "./LegSelectionContext";
import { useLegStoreState } from "./useLegStore";

// Header tri-state: checks if every visible order with ANY available leg has ALL its legs selected.
const RowLegHeaderCheckboxCell = () => {
  const { isLegWise, getVisibleOrders, onRowToggle, limitSelectAllToFilter } = useLegSelection();
  useLegStoreState();
  const [, setFilterTick] = useState(0);
  useEffect(() => {
    if (!limitSelectAllToFilter) return undefined;
    return legSelectionStore.subscribeFilter(() => setFilterTick((tick) => tick + 1));
  }, [limitSelectAllToFilter]);

  const orders = isLegWise ? getVisibleOrders() : [];
  const eligible = orders.filter((o) => Object.keys(getAvailableLegs(o)).length > 0);

  // Header checked when EVERY eligible order has at least one leg selected.
  // Indeterminate when some (but not all) orders have any selection.
  let withAnySelection = 0;
  eligible.forEach((o) => {
    const sel = legSelectionStore.getRowSnapshot(o.id);
    if (sel && Object.keys(sel).length > 0) withAnySelection += 1;
  });

  const total = eligible.length;
  const allChecked = total > 0 && withAnySelection === total;
  const indeterminate = !allChecked && withAnySelection > 0;

  const handleChange = useCallback(() => {
    if (!limitSelectAllToFilter) {
      if (allChecked) {
        eligible.forEach((o) => {
          const sel = legSelectionStore.getRowSnapshot(o.id);
          if (sel && Object.keys(sel).length > 0) onRowToggle(o.id, getAvailableLegs(o));
        });
      } else {
        eligible.forEach((o) => {
          const avail = getAvailableLegs(o);
          const sel = legSelectionStore.getRowSnapshot(o.id) || {};
          const availKeys = Object.keys(avail);
          const fully = availKeys.length === Object.keys(sel).length && availKeys.every((k) => sel[k] === avail[k]);
          if (!fully) onRowToggle(o.id, avail);
        });
      }
      return;
    }
    const currentEligible = (getVisibleOrders() || []).filter(
      (o) => o && Object.keys(getAvailableLegs(o)).length > 0,
    );
    let selectedCount = 0;
    currentEligible.forEach((o) => {
      const sel = legSelectionStore.getRowSnapshot(o.id);
      if (sel && Object.keys(sel).length > 0) selectedCount += 1;
    });
    const allNow = currentEligible.length > 0 && selectedCount === currentEligible.length;
    if (allNow) {
      currentEligible.forEach((o) => {
        const sel = legSelectionStore.getRowSnapshot(o.id);
        if (sel && Object.keys(sel).length > 0) onRowToggle(o.id, getAvailableLegs(o), { headerSelectAll: true });
      });
    } else {
      currentEligible.forEach((o) => {
        const avail = getAvailableLegs(o);
        const sel = legSelectionStore.getRowSnapshot(o.id) || {};
        const availKeys = Object.keys(avail);
        const fully = availKeys.length === Object.keys(sel).length && availKeys.every((k) => sel[k] === avail[k]);
        if (!fully) onRowToggle(o.id, avail, { headerSelectAll: true });
      });
    }
  }, [limitSelectAllToFilter, eligible, allChecked, getVisibleOrders, onRowToggle]);

  if (!isLegWise || total === 0) return null;

  return (
    <Checkbox
      checked={allChecked}
      indeterminate={indeterminate}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

export default RowLegHeaderCheckboxCell;
